/**
 * Normalisation before detection
 *
 * Downscale to a working edge, grey-world white balance, luminance conversion,
 * CLAHE for local contrast, and a bilateral pass that removes skin texture
 * without softening the rekhās.
 *
 * Colour images are `{ width, height, data }` with `data` an RGBA
 * `Uint8ClampedArray` — the shape of a browser `ImageData`, but nothing here
 * needs the DOM, so the same code runs in Node (tests), in a Worker and on the
 * main thread. Gray images are `{ width, height, data: Float32Array }` in [0,1].
 * Every function returns a new image; nothing is mutated, so intermediate stages
 * stay inspectable in the debug view.
 *
 * © Manish Jagdish Thatte
 */

import { makeGray, resizeGray } from './image.js';

export const WORKING_EDGE = 640;

/** Rec. 601 luma of an RGBA image → gray in [0,1]. */
export function toLuminance(img) {
  const out = makeGray(img.width, img.height);
  const d = img.data;
  for (let i = 0, j = 0; i < out.data.length; i++, j += 4) {
    out.data[i] = (0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2]) / 255;
  }
  return out;
}

/**
 * Grey-world white balance: scale each channel so the three channel means are
 * equal. Warm indoor light shifts skin chroma badly enough to defeat a fixed
 * skin threshold; this removes most of that shift before segmentation.
 */
export function greyWorld(img) {
  const d = img.data; const n = img.width * img.height;
  let r = 0; let g = 0; let b = 0;
  for (let j = 0; j < n * 4; j += 4) { r += d[j]; g += d[j + 1]; b += d[j + 2]; }
  r /= n; g /= n; b /= n;
  const m = (r + g + b) / 3 || 1;
  const kr = m / (r || 1); const kg = m / (g || 1); const kb = m / (b || 1);
  const out = new Uint8ClampedArray(d.length);
  for (let j = 0; j < d.length; j += 4) {
    out[j] = d[j] * kr; out[j + 1] = d[j + 1] * kg; out[j + 2] = d[j + 2] * kb; out[j + 3] = d[j + 3];
  }
  return { width: img.width, height: img.height, data: out };
}

/** Bilinear RGBA resize so that the longer edge is at most `maxEdge`. */
export function normalise(img, { maxEdge = WORKING_EDGE } = {}) {
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  if (scale === 1) return { width: img.width, height: img.height, data: Uint8ClampedArray.from(img.data), scale };
  const w = Math.max(1, Math.round(img.width * scale)); const h = Math.max(1, Math.round(img.height * scale));
  const out = new Uint8ClampedArray(w * h * 4);
  for (let c = 0; c < 4; c++) {
    const plane = makeGray(img.width, img.height);
    for (let i = 0; i < plane.data.length; i++) plane.data[i] = img.data[i * 4 + c];
    const r = resizeGray(plane, w, h);
    for (let i = 0; i < r.data.length; i++) out[i * 4 + c] = r.data[i];
  }
  return { width: w, height: h, data: out, scale };
}

/**
 * Contrast-limited adaptive histogram equalisation.
 *
 * `tiles` per side, `clip` as a multiple of the uniform bin height. Bilinear
 * interpolation between neighbouring tile mappings avoids visible tile seams,
 * which would otherwise be traced as lines.
 */
export function clahe(gray, tiles = 8, clip = 3, bins = 256) {
  const { width: w, height: h, data } = gray;
  const tw = w / tiles; const th = h / tiles;
  const maps = new Array(tiles * tiles);
  for (let ty = 0; ty < tiles; ty++) {
    for (let tx = 0; tx < tiles; tx++) {
      const x0 = Math.floor(tx * tw); const x1 = Math.min(w, Math.floor((tx + 1) * tw));
      const y0 = Math.floor(ty * th); const y1 = Math.min(h, Math.floor((ty + 1) * th));
      const hist = new Float64Array(bins);
      let count = 0;
      for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
        hist[Math.min(bins - 1, Math.max(0, Math.round(data[y * w + x] * (bins - 1))))]++; count++;
      }
      // clip and redistribute
      const limit = Math.max(1, clip * count / bins);
      let excess = 0;
      for (let i = 0; i < bins; i++) if (hist[i] > limit) { excess += hist[i] - limit; hist[i] = limit; }
      const add = excess / bins;
      let acc = 0; const cdf = new Float32Array(bins);
      for (let i = 0; i < bins; i++) { acc += hist[i] + add; cdf[i] = count ? acc / (count + excess) : i / (bins - 1); }
      maps[ty * tiles + tx] = cdf;
    }
  }
  const out = makeGray(w, h);
  for (let y = 0; y < h; y++) {
    const fy = Math.min(tiles - 1, Math.max(0, (y + 0.5) / th - 0.5));
    const ty0 = Math.floor(fy); const ty1 = Math.min(tiles - 1, ty0 + 1); const wy = fy - ty0;
    for (let x = 0; x < w; x++) {
      const fx = Math.min(tiles - 1, Math.max(0, (x + 0.5) / tw - 0.5));
      const tx0 = Math.floor(fx); const tx1 = Math.min(tiles - 1, tx0 + 1); const wx = fx - tx0;
      const b = Math.min(bins - 1, Math.max(0, Math.round(data[y * w + x] * (bins - 1))));
      const a = maps[ty0 * tiles + tx0][b] * (1 - wx) + maps[ty0 * tiles + tx1][b] * wx;
      const c = maps[ty1 * tiles + tx0][b] * (1 - wx) + maps[ty1 * tiles + tx1][b] * wx;
      out.data[y * w + x] = a * (1 - wy) + c * wy;
    }
  }
  return out;
}

/**
 * Bilateral filter: smooth with a spatial Gaussian weighted by intensity
 * similarity, so fine skin texture (small differences) is averaged away while a
 * rekhā (a large, coherent difference) is not blended into its surroundings.
 */
export function bilateral(gray, sigmaSpace = 2, sigmaRange = 0.08) {
  const { width: w, height: h, data } = gray;
  const r = Math.max(1, Math.ceil(sigmaSpace * 2));
  const spatial = new Float32Array((2 * r + 1) ** 2);
  for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
    spatial[(dy + r) * (2 * r + 1) + dx + r] = Math.exp(-(dx * dx + dy * dy) / (2 * sigmaSpace * sigmaSpace));
  }
  const out = makeGray(w, h);
  const inv = 1 / (2 * sigmaRange * sigmaRange);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const c = data[y * w + x]; let sum = 0; let wsum = 0;
    for (let dy = -r; dy <= r; dy++) {
      const yy = Math.min(h - 1, Math.max(0, y + dy));
      for (let dx = -r; dx <= r; dx++) {
        const xx = Math.min(w - 1, Math.max(0, x + dx));
        const v = data[yy * w + xx]; const dv = v - c;
        const wgt = spatial[(dy + r) * (2 * r + 1) + dx + r] * Math.exp(-dv * dv * inv);
        sum += v * wgt; wsum += wgt;
      }
    }
    out.data[y * w + x] = sum / wsum;
  }
  return out;
}
